import { Group, Text, Button, Box, Grid, ActionIcon, Flex, Container, Card, Modal, Input, Skeleton } from '@mantine/core';
import { IconHelp, IconTrash, IconPencil, IconCheck, IconX, IconCaretUpFilled, IconCaretDownFilled, IconSearch, IconCopy } from '@tabler/icons-react';
import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from "react-router-dom";
import ReactDOM from 'react-dom';
import { useDocumentTitle } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { HeaderMenu } from "../../components/HeaderMenu/HeaderMenu";
import { SelectTooltip } from "../../components/GeneralComponent/GeneralComponent";
import CampaignApi from "../../api/CampaignApi";
import PermissionWrapper from '../../PermissionWrapper';
import { DuplicateCampaign } from '../../components/CreateCampaign/DuplicateCampaign';

export function ManageCampaigns() {
  const navigate = useNavigate();
  const [selectedMemberWebsite, setSelectedMemberWebsite] = useState('');
  const [deleteCampaignId, setDeleteCampaignId] = useState('');
  const [deleteValue, setDeleteValue] = useState<string>('');
  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);
  const [error, setError] = useState('');

  const campaignIconRef = useRef(null);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [helpTargetId, setHelpTargetId] = useState(0);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });

  const [sortKeyName, setSortKeyName] = useState('');
  const [sortingDirection, setSortingDirection] = useState('');
  const [sortedCampaignList, setSortedCampaignList] = useState([]);
  const [duplicateCampaignModalOpen, setDuplicateCampaignModalOpen] = useState(false);
  const [duplicateCampaign, setDuplicateCampaign] = useState(null);
  const [campaignListRefresh, setCampaignListRefresh] = useState(0);

  const handleHeaderMenuComponentChange = (Value:any) => {
    setSelectedMemberWebsite(Value);
  };

  useDocumentTitle("Visiopt - Manage Campaigns");

  const updateCampaign = (campaign_id:any) => {
    navigate(`/create-campaign/${campaign_id}`);
  }

  const deleteCampaignConfirm = (deleteCampaignId:any) => {
    setDeleteModalIsOpen(true);
    setDeleteCampaignId(deleteCampaignId);
  };

  /* DELETE CAMPAIGN*/
  const handleDeleteCampaign = (deleteCampaignId:any) => {
    if (deleteValue.trim() === 'DELETE') {
      CampaignApi.deletecampaign({
        c_id : deleteCampaignId,
        system_website_id : selectedMemberWebsite
      })
      .then(response => {
        if (response.ok) {
          return response.json();
        } else {
          if (response.status === 401 || response.status === 400) {
            navigate("/logout");
          }
        }
      })
      .then(data => {
        if(data.status === 1){
          setDeleteModalIsOpen(false);
          setCampaignList(campaignList.filter(item => item.campaign_id !== deleteCampaignId));
          setSortedCampaignList(sortedCampaignList.filter(item => item.campaign_id !== deleteCampaignId));
          notifications.show({
            color: 'teal',
            icon: <IconCheck size="1.1rem" />,
            title: 'Success',
            message: data.message,
          });
          setDeleteValue('');
          setError('');
        } else {
          notifications.show({
            color: 'red',
            icon: <IconX size="1.1rem" />,
            title: 'Error',
            message: data.message,
          });
        }
      })
      .catch(error => {
        console.error("API Error:", error);
      });
    } else {
      setError('Word does not match.');
    }
  };
/* END DELETE CAMPAIGN*/
  
  const [campaignLoader, setCampaignLoader] = useState(false);
  const [campaignList, setCampaignList] = useState([]);
  const [showCreateButton, setShowCreateButton] = useState(true);

  useEffect(() => {
    if(selectedMemberWebsite !== ""){
      setCampaignLoader(true);
      CampaignApi.getcampaignlist({
        system_website_id : selectedMemberWebsite
      })
      .then(response => {
        if (response.ok) {
          return response.json();
        } else {
          if (response.status === 401 || response.status === 400) {
            setCampaignLoader(false);
            navigate("/logout");
          }
        }
      })
      .then(data => {
        if(data.status === 1 && data.data.campaign_list && data.data.campaign_list.length > 0){
          setCampaignList(data.data.campaign_list);
          setSortedCampaignList(data.data.campaign_list);
          setShowCreateButton(data.data.show_create_button);
        }else{
          setCampaignList([]);
          setSortedCampaignList([]);
        }
        setCampaignLoader(false);
      })
      .catch(error => {
        console.log(error);
        setCampaignLoader(false);
      });
    }
  }, [selectedMemberWebsite, navigate, campaignListRefresh]);

  const handleHelpClick = (id:any) => {
    setShowHelpModal(true);
    setHelpTargetId(id);
    if (campaignIconRef.current) {
      const { top, left, width } = campaignIconRef.current.getBoundingClientRect();
      const scrollOffset = window.scrollY;
      setTooltipPosition({
        top: top + scrollOffset,
        left: left + width + 10, 
      });
    }
  };

  const handleSorting = (keyName: string) => {
    let newDirection = sortingDirection === 'desc' ? 'asc' : 'desc';
    setSortingDirection(newDirection);
    setSortKeyName(keyName);
    
    const sortedCampaignList = [...campaignList].sort((a, b) => {
      if (!keyName) return 0;

      const valueA = a[keyName]?.toString().toLowerCase() || '';
      const valueB = b[keyName]?.toString().toLowerCase() || '';

      if (valueA < valueB) return newDirection === 'asc' ? -1 : 1;
      if (valueA > valueB) return newDirection === 'asc' ? 1 : -1;
      return 0;
    });
    setSortedCampaignList(sortedCampaignList);
  };

  const handleKeywordChange = (e) => {
    const keyword = e.target.value;

    const filteredList = campaignList.filter(data =>
      data.campaign_name?.toLowerCase().includes(keyword.toLowerCase())
    );

    setSortedCampaignList(filteredList);
  };

	return (
		<>
    <HeaderMenu HeaderMenuValue={selectedMemberWebsite} HeaderMenuOnChange={handleHeaderMenuComponentChange}/>
    {showHelpModal &&
        ReactDOM.createPortal(
          <div>
            <SelectTooltip
              isVisible={showHelpModal}
              setIsVisible={setShowHelpModal}
              id={helpTargetId}
              tooltipPosition={tooltipPosition} 
            />
          </div>,
        document.body
    )}
    <Container size="xl">
      <Card shadow="sm" padding="sm" radius="md" withBorder>
  			<Group position="apart" mb="xs">
          <Text weight={500}>MANAGE YOUR CAMPAIGNS <IconHelp ref={campaignIconRef} size="1.1rem" color="red" style={{marginBottom:-3, cursor: 'pointer'}} onClick={() => handleHelpClick(248)}/></Text>
        </Group>
        {showCreateButton && (
          <PermissionWrapper permission="operational_access">
          <Button type="button">
            <Link to="/create-campaign/new" style={{color:"white",textDecoration: "none"}}>
              Create a New Campaign
            </Link>
          </Button>
          </PermissionWrapper>
        )}
        {campaignList.length > 0 && (
          <Box
            p={4}
            pl={20}
            mt={10}
            sx={(theme) => ({
              padding: "10px",
              borderRadius: theme.radius.md,
              fontWeight: "bold",
            })}
          >
          {campaignList.some(item => Array.isArray(item) ? item.length > 0 : true) && (
            <Flex justify="space-between" align="center" style={{ width: "100%" }}>
              <Text mt={3} weight={500} onClick={() => handleSorting('campaign_name')}>
                <span style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
                  {'Campaign Name'}
                  {sortKeyName === 'campaign_name' && (
                    sortingDirection === 'asc' ? <IconCaretUpFilled /> : <IconCaretDownFilled />
                  )}
                </span>
              </Text>
              <Input icon={<IconSearch size={16} />} 
                placeholder="Search"
                maxLength={50}
                autoFocus
                onChange={handleKeywordChange}
                style={{ maxWidth: "250px" }}
              />
            </Flex>
          )}
          </Box>
        )}
        {campaignLoader ? (
          <Flex direction="column" gap="sm" mt={10}>
            {Array.from({ length: 12 }).map((_, index) => (
              <Skeleton key={index} height={44} width={1262} />
            ))}
          </Flex>
        ) : (
          sortedCampaignList.map((Campaign, index) =>
            <Box
              key={index}
              p={4}
              pl={20}
              mt={10}
              sx={(theme) => ({
                backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[5] : '#f8f9fa',
                padding: "10px",
                borderRadius: theme.radius.md,
                border: "0.0625rem solid transparent",
                borderColor: theme.colorScheme === 'dark' ? theme.colors.dark[1] : '#dee2e6'
              })}
            >
              <Grid gutter="xs">
                <Grid.Col span={6}>
                  <Text mt={3}>
                    {Campaign.campaign_name}
                  </Text>
                </Grid.Col>
                <Grid.Col span={6}>
                  <Flex
                    justify="flex-end"
                    align="center"
                    direction="row"
                    wrap="wrap"
                  >
                  <PermissionWrapper permission="operational_access">
                  <ActionIcon
                    variant="default"
                    size="lg"
                    mx={3}
                    title="Edit"
                    onClick={() => updateCampaign(Campaign.campaign_id)}
                  >
                    <IconPencil size="1.1rem" />
                  </ActionIcon>
                  <ActionIcon
                    variant="default"
                    size="lg"
                    mx={3}
                    title="Duplicate"
                    onClick={() => {
                      setDuplicateCampaign(Campaign);
                      setDuplicateCampaignModalOpen(true);
                    }}
                  >
                    <IconCopy size="1.1rem" />
                  </ActionIcon>
                  <ActionIcon
                    variant="default"
                    size="lg"
                    mx={3}
                    title="Delete"
                    onClick={() => deleteCampaignConfirm(Campaign.campaign_id)}
                  >
                    <IconTrash size="1.1rem" />
                  </ActionIcon>
                  </PermissionWrapper>
                  </Flex>
                </Grid.Col>
              </Grid>
            </Box>
          )
        )}
      </Card>
      <DuplicateCampaign
        duplicateCampaignModalOpen={duplicateCampaignModalOpen}
        setDuplicateCampaignModalOpen={setDuplicateCampaignModalOpen}
        selectedMemberWebsite={selectedMemberWebsite}
        sourceCampaign={duplicateCampaign}
        activeCampaignPageIds={campaignList.map((campaign:any) => campaign.campaign_page)}
        onDuplicated={() => setCampaignListRefresh(prev => prev + 1)}
      />
      {/*Delete Modal*/}
      <Modal
        opened={deleteModalIsOpen}
        onClose={() => {
          setDeleteModalIsOpen(false);
          setDeleteValue('');
          setError('');
        }}
        title="Delete"
        trapFocus={false}
      >
        <Text size="sm" style={{ userSelect: 'none' }}>
          Are you sure you want to delete this campaign?<br/>
          If this campaign is use in report then it will remove this campaign from it!<br/>
          If you want to proceed, please enter the word <b>DELETE</b> below:
        </Text>
        <Input
          mt={10}
          mb={10}
          value={deleteValue}
          onChange={(e) => {
            const value = e.currentTarget.value;
            setDeleteValue(value);
            if (value.trim() === 'DELETE') {
              setError('');
            }
          }}
        />
        {error && (
          <Text color="red" size="sm" mt={10}>
            {error}
          </Text>
        )}
        <Group position="right">
          <Button onClick={() => handleDeleteCampaign(deleteCampaignId)}>OK</Button>
        </Group>
      </Modal>
    </Container>
		</>
	);
}